/* eslint-disable jsx-a11y/no-autofocus --
 * The single autoFocus in this file moves focus into a control the user just
 * summoned (an inline editor that appears on their own click, or a modal's
 * first field). That is required focus management: leaving focus behind
 * strands keyboard users on an element that has since vanished. The rule
 * guards against autofocus on PAGE LOAD, which is a different thing and is
 * not done anywhere here. File-scoped rather than line-scoped because
 * Prettier reformats JSX props across lines, which silently detaches a
 * line-targeted disable from what it was suppressing.
 */
import {
  CheckOutlined,
  CloseOutlined,
  ControlOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  FileOutlined,
  PlusOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { Button, Checkbox, Drawer, Input, InputNumber, Switch, Tooltip, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type AdditionalDocumentEntry,
  DOCUMENT_MAX_FILE_SIZE_WARNING_THRESHOLD_MB,
  type FileEntryCondition,
  MAX_DOCUMENT_MAX_FILE_SIZE_MB,
  MIN_DOCUMENT_MAX_FILE_SIZE_MB,
  type WorkflowStep,
  workflowsApi,
} from '@/api/workflows';
import { ConfirmPopover } from '@/components/ConfirmPopover';
import { ICON_BTN_PROPS } from '@/components/iconButton';
import { handleApiError } from '@/lib/handleApiError';

import { ConditionsEditorModal } from './conditions/ConditionsEditorModal';

const { Text } = Typography;

const I18N_NS = 'workflow.steps.additionalDocuments';
// BMO blue — matches the condition indicator used on the step rail
// (AntD primary) and on the PDF Entries drawer's per-entry button so the
// "has conditions" cue is one colour across the editor.
const TINT_FG = '#0179C1';

// Slightly smaller than the global 32x32 icon button — these sit in a tight
// header row inside each entry card. Mirrors PdfEntriesDrawer's `iconBtn`.
const iconBtn = {
  ...ICON_BTN_PROPS,
  style: { ...ICON_BTN_PROPS.style, width: 26, height: 26, fontSize: 12, borderRadius: 6 },
} as const;

/**
 * Supported extensions an originator can permit per document entry. Keep in sync
 * with WorkflowStep.SupportedExtensions on the BE — the BE re-validates at runtime so
 * a tampered FE can't bypass this list. Grouping is purely UX; the value sent is the
 * raw extension string (lowercase, no dot).
 */
const EXTENSION_GROUPS: ReadonlyArray<{ groupKey: string; values: ReadonlyArray<string> }> = [
  { groupKey: 'documents', values: ['pdf', 'doc', 'docx', 'txt'] },
  { groupKey: 'spreadsheets', values: ['xls', 'xlsx', 'csv'] },
  { groupKey: 'images', values: ['jpg', 'jpeg', 'png', 'webp'] },
];

type Props = {
  templateId: string;
  step: WorkflowStep | null;
  /** All steps in the workflow — passed in so the per-document condition modal
   *  can enumerate selectable source fields across every DynamicForm step. */
  allSteps: WorkflowStep[];
  editable: boolean;
  onClose: () => void;
  onChanged: () => Promise<void>;
};

/** True when the entry carries at least one gating condition. */
function hasCondition(entry: AdditionalDocumentEntry): boolean {
  return (entry.conditions?.length ?? 0) > 0;
}

/**
 * Free-form max-file-size input shared by the per-entry cards and the
 * step-level optional bucket. Replaces the old 10/15/20 MB `Segmented`
 * picker now that the BE accepts any value in
 * [{@link MIN_DOCUMENT_MAX_FILE_SIZE_MB}, {@link MAX_DOCUMENT_MAX_FILE_SIZE_MB}].
 *
 * Types into local `draft` state and only calls `onCommit` on blur/Enter —
 * committing per keystroke would fire a PATCH per digit and briefly send
 * out-of-range intermediate values (e.g. the "2" in "20"). `draft` resyncs
 * from `value` whenever it changes, which also covers the caller's
 * optimistic-update rollback (its `value` reverts on a failed PATCH).
 *
 * Deliberately skips InputNumber's `min`/`max` props: those auto-clamp the
 * typed value on blur with no feedback, which reads as the number silently
 * changing under the originator. Out-of-range input instead gets an inline
 * error and is never committed — no popup, no silent correction.
 */
function MaxFileSizeInput({
  value,
  onCommit,
  disabled,
}: {
  value: number;
  onCommit: (mb: number) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<number | null>(value);
  useEffect(() => setDraft(value), [value]);

  const isOutOfRange =
    draft === null ||
    draft < MIN_DOCUMENT_MAX_FILE_SIZE_MB ||
    draft > MAX_DOCUMENT_MAX_FILE_SIZE_MB;

  const commit = () => {
    if (isOutOfRange) return;
    if (draft !== value) onCommit(draft);
  };

  const showWarning = !isOutOfRange && draft > DOCUMENT_MAX_FILE_SIZE_WARNING_THRESHOLD_MB;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Text strong style={{ fontSize: 13, color: '#111827' }}>
          {t(`${I18N_NS}.maxFileSize`)}
        </Text>
        <InputNumber
          size="small"
          precision={0}
          status={isOutOfRange ? 'error' : undefined}
          value={draft}
          disabled={disabled}
          onChange={(v) => setDraft(v)}
          onBlur={commit}
          onPressEnter={commit}
          addonAfter="MB"
          style={{ width: 110 }}
        />
      </div>
      {isOutOfRange && (
        <Text type="danger" style={{ fontSize: 12 }}>
          {t(`${I18N_NS}.maxFileSizeError`, {
            min: MIN_DOCUMENT_MAX_FILE_SIZE_MB,
            max: MAX_DOCUMENT_MAX_FILE_SIZE_MB,
          })}
        </Text>
      )}
      {showWarning && (
        <Text type="warning" style={{ fontSize: 12 }}>
          {t(`${I18N_NS}.maxFileSizeWarning`, {
            threshold: DOCUMENT_MAX_FILE_SIZE_WARNING_THRESHOLD_MB,
          })}
        </Text>
      )}
    </div>
  );
}

/**
 * Drawer for configuring an AdditionalDocuments step. Replicates the
 * PdfFileUpload drawer's entry-list pattern (PdfEntriesDrawer): the
 * originator adds named document slots, each with its own accepted file
 * types, a required flag, and an opt-out of the platform's default
 * non-PDF -> PDF conversion. Unlike PdfFileUpload, the originator never
 * uploads a file here — the advisor supplies it at runtime.
 */
export function AdditionalDocumentEntriesDrawer({
  templateId,
  step,
  allSteps,
  editable,
  onClose,
  onChanged,
}: Props) {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language.startsWith('fr');
  const [conditionTarget, setConditionTarget] = useState<AdditionalDocumentEntry | null>(null);
  // New entries default to expanded so the originator lands on the file-type
  // picker right after clicking Add Document.
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  // Optimistic mirror for the optional-bucket size picker — paints the new
  // pill instantly instead of waiting for PUT → refetch → re-render. Resets
  // whenever the step prop's persisted value changes.
  const [optimisticBucketMaxSize, setOptimisticBucketMaxSize] = useState<number | null>(null);
  const stepBucketMaxSize = step?.optionalDocumentMaxFileSizeMb;
  useEffect(() => setOptimisticBucketMaxSize(null), [stepBucketMaxSize]);
  // Same optimistic-mirror pattern as the size picker above — paints the
  // Switch instantly instead of waiting for PUT → refetch → re-render.
  const [optimisticShowSection, setOptimisticShowSection] = useState<boolean | null>(null);
  const stepShowSection = step?.showOptionalDocumentsSection;
  useEffect(() => setOptimisticShowSection(null), [stepShowSection]);

  if (!step) return null;

  const stepTitle = (isFr ? step.titleFr : step.titleEn) || step.titleEn || step.titleFr;
  const entries = step.additionalDocumentEntries ?? [];

  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleAddDocument = async () => {
    setCreating(true);
    try {
      const created = await workflowsApi.addAdditionalDocumentEntry(templateId, step.id, {
        label: t(`${I18N_NS}.defaultLabel`),
      });
      setExpandedIds((prev) => new Set(prev).add(created.id));
      await onChanged();
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.addFailed`));
    } finally {
      setCreating(false);
    }
  };

  const handleOptionalBucketExtensionsChange = async (extensions: string[]) => {
    try {
      await workflowsApi.setOptionalDocumentExtensions(templateId, step.id, extensions);
      await onChanged();
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  const handleOptionalBucketMaxFileSizeChange = async (mb: number) => {
    setOptimisticBucketMaxSize(mb);
    try {
      await workflowsApi.setOptionalDocumentExtensions(
        templateId,
        step.id,
        step.optionalDocumentExtensions ?? [],
        mb,
      );
      await onChanged();
    } catch (err) {
      setOptimisticBucketMaxSize(null);
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  const handleShowSectionChange = async (checked: boolean) => {
    setOptimisticShowSection(checked);
    try {
      await workflowsApi.setOptionalDocumentExtensions(
        templateId,
        step.id,
        step.optionalDocumentExtensions ?? [],
        undefined,
        checked,
      );
      await onChanged();
    } catch (err) {
      setOptimisticShowSection(null);
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  return (
    <Drawer open={!!step} onClose={onClose} title={stepTitle} width={640} destroyOnHidden>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          loading={creating}
          disabled={!editable}
          onClick={handleAddDocument}
          style={{ alignSelf: 'flex-start', borderRadius: 999, padding: '0 18px' }}
        >
          {t(`${I18N_NS}.addDocument`)}
        </Button>

        {entries.length === 0 && (
          <Text type="secondary" style={{ fontSize: 13 }}>
            {t(`${I18N_NS}.empty`)}
          </Text>
        )}

        {entries.map((entry) => (
          <EntryCard
            key={entry.id}
            templateId={templateId}
            stepId={step.id}
            entry={entry}
            editable={editable}
            isExpanded={expandedIds.has(entry.id)}
            onToggleExpanded={() => toggleExpanded(entry.id)}
            onConfigureCondition={setConditionTarget}
            onChanged={onChanged}
          />
        ))}

        {/* Optional-bucket section — visually framed as its own card so it
            reads as a distinct concern from the named-entry list above.
            Mirrors the EntryCard's chrome (light border, rounded corners,
            soft-blue header strip) for visual consistency. */}
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 12,
            background: '#fff',
            marginTop: 8,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              background: '#f8fafc',
              padding: '12px 16px',
              borderBottom: '1px solid #e5e7eb',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}
            >
              <Text strong style={{ fontSize: 14, color: '#111827' }}>
                {t(`${I18N_NS}.optionalBucketDivider`)}
              </Text>
              <Tooltip title={t(`${I18N_NS}.showSectionTooltip`)}>
                <Switch
                  size="small"
                  checked={optimisticShowSection ?? step.showOptionalDocumentsSection}
                  onChange={(checked) => void handleShowSectionChange(checked)}
                  disabled={!editable}
                />
              </Tooltip>
            </div>
            <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
              {t(`${I18N_NS}.optionalBucketDescription`)}
            </Text>
          </div>
          <Checkbox.Group
            value={step.optionalDocumentExtensions}
            onChange={(vals) => handleOptionalBucketExtensionsChange(vals as string[])}
            disabled={!editable}
            style={{ width: '100%' }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 18,
                padding: '14px 16px 16px',
              }}
            >
              {EXTENSION_GROUPS.map(({ groupKey, values }) => (
                <div key={groupKey}>
                  <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                    {t(`${I18N_NS}.groups.${groupKey}`)}
                  </Text>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                    {values.map((ext) => (
                      <Checkbox key={ext} value={ext} style={{ alignItems: 'center' }}>
                        {`.${ext}`}
                      </Checkbox>
                    ))}
                  </div>
                </div>
              ))}
              {/* Step-level size cap for the optional bucket. Same input the
                  per-entry cards use; persists via the same setOptional
                  endpoint that handles the extension list. */}
              <div
                style={{
                  paddingTop: 6,
                  borderTop: '1px solid #e5e7eb',
                  marginTop: 4,
                }}
              >
                <MaxFileSizeInput
                  value={optimisticBucketMaxSize ?? step.optionalDocumentMaxFileSizeMb}
                  onCommit={(mb) => void handleOptionalBucketMaxFileSizeChange(mb)}
                  disabled={!editable}
                />
              </div>
            </div>
          </Checkbox.Group>
        </div>
      </div>

      <ConditionModal
        templateId={templateId}
        stepId={step.id}
        entry={conditionTarget}
        allSteps={allSteps}
        onClose={() => setConditionTarget(null)}
        onSaved={async () => {
          setConditionTarget(null);
          await onChanged();
        }}
      />
    </Drawer>
  );
}

/**
 * One document entry. Collapses into a single row (name + actions + a
 * summary of accepted file types); expands into a detail panel with the
 * file-type checkboxes and the "don't parse to PDF" override. No file
 * upload happens here — the advisor supplies the file at runtime.
 */
function EntryCard({
  templateId,
  stepId,
  entry,
  editable,
  isExpanded,
  onToggleExpanded,
  onConfigureCondition,
  onChanged,
}: {
  templateId: string;
  stepId: string;
  entry: AdditionalDocumentEntry;
  editable: boolean;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  onConfigureCondition: (entry: AdditionalDocumentEntry) => void;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.label);
  const [saving, setSaving] = useState(false);
  // Optimistic mirror of the max-size segment so the click registers
  // instantly instead of waiting for PATCH → refetch → re-render. Resets to
  // the persisted value whenever the entry prop changes (refetch landed).
  const [optimisticMaxSize, setOptimisticMaxSize] = useState<number | null>(null);
  useEffect(() => setOptimisticMaxSize(null), [entry.maxFileSizeMb]);

  const persist = async (
    patch: Partial<
      Pick<
        AdditionalDocumentEntry,
        'label' | 'allowedExtensions' | 'isRequired' | 'doNotParseToPdf' | 'maxFileSizeMb'
      >
    >,
  ) => {
    await workflowsApi.editAdditionalDocumentEntry(templateId, stepId, entry.id, {
      label: patch.label ?? entry.label,
      allowedExtensions: patch.allowedExtensions ?? entry.allowedExtensions,
      isRequired: patch.isRequired ?? entry.isRequired,
      doNotParseToPdf: patch.doNotParseToPdf ?? entry.doNotParseToPdf,
      maxFileSizeMb: patch.maxFileSizeMb ?? entry.maxFileSizeMb,
    });
    await onChanged();
  };

  const commitRename = async () => {
    const next = draft.trim();
    if (!next || next === entry.label) {
      setEditing(false);
      setDraft(entry.label);
      return;
    }
    setSaving(true);
    try {
      await persist({ label: next });
      setEditing(false);
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await workflowsApi.deleteAdditionalDocumentEntry(templateId, stepId, entry.id);
      await onChanged();
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.deleteFailed`));
    }
  };

  const handleToggleRequired = async (checked: boolean) => {
    try {
      await persist({ isRequired: checked });
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  const handleExtensionsChange = async (extensions: string[]) => {
    try {
      await persist({ allowedExtensions: extensions });
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  const handleDoNotParseChange = async (checked: boolean) => {
    try {
      await persist({ doNotParseToPdf: checked });
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  const handleMaxFileSizeChange = async (mb: number) => {
    // Optimistic: paint the new pill on click, fire the PATCH in the
    // background. If it fails, drop the optimistic state so the picker
    // snaps back to whatever the BE has.
    setOptimisticMaxSize(mb);
    try {
      await persist({ maxFileSizeMb: mb });
    } catch (err) {
      setOptimisticMaxSize(null);
      handleApiError(err, t, t(`${I18N_NS}.editFailed`));
    }
  };

  return (
    <div
      style={{
        border: '1px solid #e5e7eb',
        borderRadius: 12,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* Header row — name + actions. Always visible. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 14px',
          background: '#f8fafc',
          borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none',
        }}
      >
        <div style={{ flex: 1, minWidth: 0, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {editing ? (
            <Input
              autoFocus
              size="small"
              value={draft}
              disabled={saving}
              maxLength={120}
              onChange={(e) => setDraft(e.target.value)}
              onPressEnter={commitRename}
              style={{ maxWidth: 320 }}
            />
          ) : (
            <Text strong style={{ fontSize: 14, color: '#111827' }}>
              {entry.label}
            </Text>
          )}
          {!editing && (
            <Tooltip title={t(`${I18N_NS}.rename`)}>
              <Button
                {...iconBtn}
                icon={<EditOutlined />}
                disabled={!editable}
                onClick={() => setEditing(true)}
              />
            </Tooltip>
          )}
          {editing && (
            <>
              <Tooltip title={t('common.save')}>
                <Button
                  {...iconBtn}
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={saving}
                  onClick={commitRename}
                />
              </Tooltip>
              <Tooltip title={t('common.cancel')}>
                <Button
                  {...iconBtn}
                  icon={<CloseOutlined />}
                  disabled={saving}
                  onClick={() => {
                    setEditing(false);
                    setDraft(entry.label);
                  }}
                />
              </Tooltip>
            </>
          )}
          {hasCondition(entry) && !editing && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t(`${I18N_NS}.conditionallyN`, { count: entry.conditions.length })}
            </Text>
          )}
        </div>

        <Tooltip
          title={t(hasCondition(entry) ? `${I18N_NS}.conditionEdit` : `${I18N_NS}.conditionAdd`)}
        >
          <Button
            {...iconBtn}
            icon={<ControlOutlined style={{ color: hasCondition(entry) ? TINT_FG : undefined }} />}
            disabled={!editable}
            onClick={() => onConfigureCondition(entry)}
          />
        </Tooltip>

        <ConfirmPopover
          title={t(`${I18N_NS}.deleteConfirmTitle`)}
          description={t(`${I18N_NS}.deleteConfirmDesc`)}
          onConfirm={handleDelete}
          disabled={!editable}
        >
          <Tooltip title={t(`${I18N_NS}.delete`)}>
            <Button {...iconBtn} danger icon={<DeleteOutlined />} disabled={!editable} />
          </Tooltip>
        </ConfirmPopover>

        <Tooltip title={t(`${I18N_NS}.requiredTooltip`)}>
          <Switch
            size="small"
            checked={entry.isRequired}
            onChange={handleToggleRequired}
            disabled={!editable}
          />
        </Tooltip>

        <Tooltip title={t(isExpanded ? `${I18N_NS}.collapse` : `${I18N_NS}.expand`)}>
          <Button
            {...iconBtn}
            icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
            onClick={onToggleExpanded}
          />
        </Tooltip>
      </div>

      {/* Collapsed body — compact summary of accepted file types + parse override. */}
      {!isExpanded && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 14px',
            flexWrap: 'wrap',
          }}
        >
          {entry.allowedExtensions.length > 0 ? (
            entry.allowedExtensions.map((ext) => (
              <Text
                key={ext}
                style={{
                  fontSize: 12,
                  padding: '2px 8px',
                  background: '#f8fafc',
                  border: '1px solid #e5e7eb',
                  borderRadius: 999,
                }}
              >
                {`.${ext}`}
              </Text>
            ))
          ) : (
            <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
              {t(`${I18N_NS}.noTypesSelected`)}
            </Text>
          )}
          {entry.doNotParseToPdf && (
            <Text
              style={{
                fontSize: 12,
                padding: '2px 8px',
                background: '#FFFBEB',
                border: '1px solid #FDE68A',
                borderRadius: 999,
                color: '#92400E',
              }}
            >
              <FileOutlined style={{ marginRight: 4 }} />
              {t(`${I18N_NS}.notParsedBadge`)}
            </Text>
          )}
        </div>
      )}

      {/* Expanded body — file-type selector + the "don't parse to PDF" override.
          No file upload here; the advisor supplies the actual file at runtime. */}
      {isExpanded && (
        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <Checkbox.Group
            value={entry.allowedExtensions}
            onChange={(vals) => handleExtensionsChange(vals as string[])}
            disabled={!editable}
            style={{ width: '100%' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {EXTENSION_GROUPS.map(({ groupKey, values }) => (
                <div key={groupKey}>
                  <Text strong style={{ display: 'block', marginBottom: 8, fontSize: 13 }}>
                    {t(`${I18N_NS}.groups.${groupKey}`)}
                  </Text>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
                    {values.map((ext) => (
                      <Checkbox key={ext} value={ext}>
                        {`.${ext}`}
                      </Checkbox>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Checkbox.Group>

          {/* Visual separator between the per-format allow-list (above) and the
              size + conversion-bypass row (below) so the two concerns read as
              distinct. */}
          <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 4 }} />

          {/* Per-entry combined file-size cap. */}
          <MaxFileSizeInput
            value={optimisticMaxSize ?? entry.maxFileSizeMb}
            onCommit={(mb) => void handleMaxFileSizeChange(mb)}
            disabled={!editable}
          />

          <Checkbox
            checked={entry.doNotParseToPdf}
            disabled={!editable}
            onChange={(e) => handleDoNotParseChange(e.target.checked)}
            // Center the box vertically with the label text — AntD's default
            // baseline alignment leaves the text sitting slightly below the
            // box on this font + size combination.
            style={{ alignItems: 'center' }}
          >
            {t(`${I18N_NS}.doNotParseToPdf`)}
          </Checkbox>
        </div>
      )}
    </div>
  );
}

/**
 * Thin wrapper around the shared ConditionsEditorModal — same model the
 * field/section/PDF visibility editors use. Owns persistence for the
 * additional-document case.
 */
function ConditionModal({
  templateId,
  stepId,
  entry,
  allSteps,
  onClose,
  onSaved,
}: {
  templateId: string;
  stepId: string;
  entry: AdditionalDocumentEntry | null;
  allSteps: WorkflowStep[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const isOpen = entry !== null;
  const handleSave = async (conditions: FileEntryCondition[], combinator: 'all' | 'any' | null) => {
    if (!entry) return;
    setSaving(true);
    try {
      await workflowsApi.setAdditionalDocumentEntryConditions(templateId, stepId, entry.id, {
        conditions,
        combinator,
      });
      await onSaved();
    } catch (err) {
      handleApiError(err, t, t(`${I18N_NS}.conditionSaveFailed`));
    } finally {
      setSaving(false);
    }
  };
  return (
    <ConditionsEditorModal
      open={isOpen}
      title={t(`${I18N_NS}.conditionModalTitle`)}
      intro={t(`${I18N_NS}.conditionIntro`)}
      initialConditions={entry?.conditions ?? []}
      initialCombinator={entry?.conditionCombinator ?? null}
      allSteps={allSteps}
      saving={saving}
      onCancel={onClose}
      onSave={handleSave}
    />
  );
}
